import React, { useEffect, useState } from "react";
import dummyImg from "../../assets/images/dummy.png";
import { CircularProgressbar } from "react-circular-progressbar";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { Button, Dropdown, Image, Modal } from "react-bootstrap";
import {
  adminApprovedCertificateAPI,
  adminRejectCertificateAPI,
  getAllCertificateByAdminAPI,
} from "../../redux/features/adminSlice";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import DottedLoading from "../DottedLoading/DottedLoading";
import { Link } from "react-router-dom";

const CertificatesApprovalsTable = ({
  fetchAPI = getAllCertificateByAdminAPI,
  approveAPI = adminApprovedCertificateAPI,
  rejectAPI = adminRejectCertificateAPI,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [certificatesApprovals, setCertificatesApprovals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredStatus, setFilteredStatus] = useState("all");
  const [error, setError] = useState(null);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [declineItems, setDeclineItems] = useState(null);

  const [declineReason, setDeclineReason] = useState("");
  const [declineError, setDeclineError] = useState("");
  const [declineLoading, setDeclineLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await dispatch(
          fetchAPI({ token: auth.token })
        ).unwrap();
        if (res.success) {
          setCertificatesApprovals(res.data);
        }
      } catch (error) {
        toast.error(
          typeof error === "object" ? error.message : error || "Failed to fetch data",
          {
            position: "top-center",
          }
        );
        setError(error || "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch, auth.token]);

  const filteredCertificatesApprovals = certificatesApprovals.filter((item) => {
    if (filteredStatus === "all") return true;

    if (filteredStatus === "requested" && item.status === null) {
      return true;
    }

    return item.status?.toLowerCase() === filteredStatus;
  });

  const totalPages = Math.ceil(
    filteredCertificatesApprovals.length / rowsPerPage
  );
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentCertificatesApprovals = filteredCertificatesApprovals.slice(
    startIndex,
    endIndex
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  if (error) {
    return (
      <div className="alert alert-danger">
        {typeof error === "object" ? error.message : error}
      </div>
    );
  }

  const handleShow = (data) => {
    setDeclineItems(data);
    setShow(true);
  };

  const handleClose = () => setShow(false);

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleApprove = async () => {
    if (!selectedItem) return;
    try {
      const res = await dispatch(
        approveAPI({
          token: auth.token,
          certificateId: selectedItem?.id,
        })
      ).unwrap();
      if (res?.data?.success) {
        toast.success(
          res?.data?.message || "Certificate Approved Successfully"
        );

        const updatedCertificate = await dispatch(
          fetchAPI({ token: auth.token })
        ).unwrap();
        if (updatedCertificate.success) {
          setCertificatesApprovals(updatedCertificate.data);
        }
      }
    } catch (error) {
      toast.error(typeof error === "object" ? error.message : error || "Failed to approve");
    }
  };

  const handleDecline = async () => {
    if (!declineItems) return;
    if (!declineReason.trim()) {
      setDeclineError("Reason is required");
      return;
    }

    setDeclineError("");
    setDeclineLoading(true);

    try {
      const res = await dispatch(
        rejectAPI({
          token: auth.token,
          certificateId: declineItems?.id,
          reason: declineReason,
        })
      ).unwrap();

      if (res?.data?.success) {
        toast.success(
          res?.data?.message || "Certificate Declined Successfully"
        );

        const updatedCertificate = await dispatch(
          fetchAPI({ token: auth.token })
        ).unwrap();
        if (updatedCertificate.success) {
          setCertificatesApprovals(updatedCertificate.data);
        }

        setShow(false);
        setDeclineReason("");
      }
    } catch (error) {
      toast.error(typeof error === "object" ? error.message : error || "Failed to decline");
    } finally {
      setDeclineLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="loader-container">
          <div className="dottedLoading theme-color"></div>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-content-center mb-16">
            <h2 className="ah-heading-04 fw-bold text-black mb-0">
              Certificates
            </h2>
            <div>
              <select
                name="status"
                className="form-control"
                value={filteredStatus}
                onChange={(e) => {
                  setFilteredStatus(e.target.value);
                }}
              >
                <option value="all">All</option>
                <option value="requested">Requested</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="card-default-style px-14 py-14 py-sm-40">
            {/* Rows per page mobile */}
            <div className="align-item-center d-flex justify-content-between d-sm-none mb-10">
              <label className="me-2 flex-shrink-0 align-self-center">
                No of Rows
              </label>
              <select
                className="form-control ms-16"
                value={rowsPerPage}
                onChange={handleRowsChange}
                style={{ width: "100px" }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
                <option value={50}>50</option>
              </select>
            </div>
            {/* Table */}
            <div className="table-responsive mb-10">
              <table className="table student-table custom-table align-middle">
                <thead className="position-sticky top-0 z-1">
                  <tr>
                    <th className="ah-text-base">Sr No.</th>
                    <th className="ah-text-base">Name</th>
                    <th className="ah-text-base">Course Name</th>
                    <th className="ah-text-base" width="15%">Status</th>
                    <th className="ah-text-base" width="20%">
                      Marks
                    </th>
                    <th className="ah-text-base" width="10%">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentCertificatesApprovals.length > 0 ? (
                    currentCertificatesApprovals.map((items, index) => (
                      <tr key={items.id}>
                        <td>
                          <div className="counter-block">
                            {startIndex + index + 1}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="img-holder me-15">
                              <Image
                                src={
                                  items?.user?.profile_picture || items?.student?.profilePicture
                                    ? process.env.REACT_APP_GALLERY_URL +
                                      (items?.user?.profile_picture || items?.student?.profilePicture)
                                    : dummyImg
                                }
                                alt="items"
                                className="img-fluid h-100 w-100"
                                onError={(e) => {
                                  e.target.onError = null;
                                  e.target.src = dummyImg;
                                }}
                              />
                            </div>
                            <strong className="ah-text-sm fw-semibold">
                              {items?.user?.full_name || items?.student?.fullName}
                            </strong>
                          </div>
                        </td>
                        <td className="fw-medium">
                          <div
                            className="text-truncate"
                            style={{ maxWidth: "400px" }}
                          >
                            {items?.course?.title || items?.courseTitle}
                          </div>
                        </td>
                        <td className="fw-medium text-capitalize">{items?.status || "-"}</td>
                        <td className="fw-medium">
                          <CircularProgressbar
                            className="progress-circle-holder"
                            value={parseInt(items?.percentage + "%")}
                            strokeWidth={16}
                            text={items?.percentage + "%"}
                            styles={{
                              path: {
                                stroke:
                                  parseInt(items.percentage + "%") === 100
                                    ? "#82BD00"
                                    : "#F05203",
                              },
                              trail: {
                                stroke:
                                  parseInt(items.percentage + "%") === 100
                                    ? "#E3EBD6"
                                    : "#F0520333",
                              },
                              text: {
                                fill: "#000",
                                fontSize: "22px",
                              },
                            }}
                          />
                        </td>
                        <td className="fw-medium">
                          <Dropdown className="custom-dropdown">
                            <Dropdown.Toggle
                              variant="light"
                              className="icon-btn py-2 px-4 m-0"
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M9 5.71875C9.60406 5.71875 10.0938 5.22906 10.0938 4.625C10.0938 4.02094 9.60406 3.53125 9 3.53125C8.39594 3.53125 7.90625 4.02094 7.90625 4.625C7.90625 5.22906 8.39594 5.71875 9 5.71875Z"
                                  fill="#f05203"
                                />
                                <path
                                  d="M9 10.0938C9.60406 10.0938 10.0938 9.60406 10.0938 9C10.0938 8.39594 9.60406 7.90625 9 7.90625C8.39594 7.90625 7.90625 8.39594 7.90625 9C7.90625 9.60406 8.39594 10.0938 9 10.0938Z"
                                  fill="#f05203"
                                />
                                <path
                                  d="M9 14.4688C9.60406 14.4688 10.0938 13.9791 10.0938 13.375C10.0938 12.7709 9.60406 12.2812 9 12.2812C8.39594 12.2812 7.90625 12.7709 7.90625 13.375C7.90625 13.9791 8.39594 14.4688 9 14.4688Z"
                                  fill="#f05203"
                                />
                              </svg>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item as={Link} to={`/student-certificates-detail?certificateId=${items?.id}`}>
                                <svg
                                  width="20"
                                  height="13"
                                  viewBox="0 0 20 13"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10 1.05298C15.9008 1.05298 18.625 6.5 18.625 6.5C18.625 6.5 15.9008 11.947 10 11.947C4.09917 11.947 1.375 6.5 1.375 6.5C1.375 6.5 4.09917 1.05298 10 1.05298Z"
                                    stroke="#F05203"
                                    strokeWidth="1.16769"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M12.5746 6.50004C12.5825 6.85345 12.5197 7.20488 12.3899 7.5337C12.2602 7.86251 12.066 8.16209 11.8188 8.41483C11.5717 8.66757 11.2765 8.86839 10.9507 9.00549C10.6249 9.1426 10.2749 9.21322 9.92142 9.21322C9.56792 9.21322 9.21798 9.1426 8.89215 9.00549C8.56632 8.86839 8.27116 8.66757 8.02401 8.41483C7.77686 8.16209 7.58269 7.86251 7.4529 7.5337C7.32312 7.20488 7.26034 6.85345 7.26824 6.50004C7.26824 5.79619 7.54784 5.12118 8.04553 4.62348C8.54323 4.12579 9.21824 3.84619 9.92209 3.84619C10.6259 3.84619 11.3009 4.12579 11.7986 4.62348C12.2963 5.12118 12.5759 5.79619 12.5759 6.50004H12.5746Z"
                                    stroke="#F05203"
                                    strokeWidth="1.16769"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                View
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                className="py-4 px-10"
                                disabled={
                                  items?.status === "rejected" ||
                                  items?.status === "approved" ||
                                  items?.status === "completed"
                                }
                                onClick={() => handleOpenModal(items)}
                              >
                                <svg
                                  width="21"
                                  height="22"
                                  viewBox="0 0 21 22"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3.2666 13.7893L1.34961 11.8723C1.13143 11.654 1.00348 11.3613 0.990234 11.053C0.978694 10.7833 1.0555 10.5183 1.20801 10.2981L1.27832 10.2063L1.35742 10.1194L3.09082 8.38599L3.2666 8.21118V5.50024C3.26666 5.18924 3.38421 4.88939 3.5957 4.66138C3.80715 4.43348 4.09724 4.29419 4.40723 4.27075L4.50195 4.26685H7.21094L7.38672 4.09106L9.12695 2.34888L9.12793 2.34985C9.34614 2.13193 9.63819 2.0038 9.94629 1.99048C10.2507 1.9774 10.5493 2.07708 10.7842 2.27075L10.8809 2.35864L12.6143 4.09106L12.79 4.26685H15.5C15.811 4.2669 16.1108 4.38439 16.3389 4.59595C16.5668 4.80748 16.7061 5.09738 16.7295 5.40747L16.7334 5.50024V8.21118L16.9092 8.38599L18.6504 10.1282C18.8685 10.3464 18.9974 10.6383 19.0107 10.9465C19.0239 11.2533 18.9213 11.5536 18.7246 11.7893L18.6328 11.8889L16.7324 13.7893V16.5002C16.7325 16.8114 16.6149 17.111 16.4033 17.3391C16.1944 17.5643 15.9093 17.7028 15.6035 17.7288L15.4805 17.7336H12.79L10.873 19.6506C10.6548 19.8687 10.3629 19.9967 10.0547 20.01C9.74632 20.0233 9.44326 19.9206 9.20703 19.7219L9.12012 19.6418L7.21191 17.7336H4.5C4.18891 17.7337 3.88926 17.616 3.66113 17.4045C3.43596 17.1957 3.29654 16.9104 3.27051 16.6047L3.2666 16.4797V13.7893Z"
                                    stroke="#F05203"
                                    strokeWidth="1.2"
                                  />
                                  <path
                                    d="M13.4629 8.43506C13.583 8.43612 13.6982 8.48486 13.7832 8.56982C13.868 8.65478 13.9159 8.77007 13.917 8.89014C13.9179 9.01017 13.8705 9.12606 13.7871 9.2124L9.32031 13.6812C9.26937 13.732 9.20907 13.7727 9.14258 13.8003C9.07583 13.8279 9.00389 13.8423 8.93164 13.8423C8.85941 13.8423 8.78743 13.8279 8.7207 13.8003C8.65422 13.7727 8.5939 13.732 8.54297 13.6812L6.33887 11.4771C6.25287 11.3911 6.20417 11.2744 6.2041 11.1528C6.20406 11.0312 6.25291 10.9146 6.33887 10.8286C6.42482 10.7426 6.54151 10.6939 6.66309 10.6938C6.78459 10.6939 6.90136 10.7427 6.9873 10.8286L8.93164 12.7729L13.1406 8.56396C13.2271 8.48056 13.3428 8.43401 13.4629 8.43506Z"
                                    fill="black"
                                    stroke="#F05203"
                                    strokeWidth="0.916667"
                                  />
                                </svg>
                                Approved
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                className="py-4 px-10"
                                disabled={
                                  items?.status === "rejected" ||
                                  items?.status === "approved" ||
                                  items?.status === "completed"
                                }
                                onClick={() => handleShow(items)}
                              >
                                <svg
                                  width="16"
                                  height="17"
                                  viewBox="0 0 16 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <g clip-path="url(#clip0_6416_10007)">
                                    <path
                                      d="M16 8.5C16 4.0816 12.4184 0.5 8 0.5C3.5816 0.5 0 4.0816 0 8.5C0 12.9184 3.5816 16.5 8 16.5C12.4184 16.5 16 12.9184 16 8.5ZM14.4 8.5C14.4001 9.69768 14.0642 10.8714 13.4304 11.8876C12.7966 12.9038 11.8903 13.7218 10.8147 14.2485C9.73905 14.7753 8.53719 14.9896 7.34579 14.8672C6.15438 14.7448 5.02123 14.2905 4.0752 13.556L13.056 4.5752C13.929 5.69707 14.402 7.07849 14.4 8.5ZM11.9248 3.444L2.944 12.4248C1.98678 11.1935 1.51213 9.65497 1.60923 8.09835C1.70633 6.54174 2.3685 5.07416 3.47133 3.97133C4.57416 2.8685 6.04174 2.20633 7.59835 2.10923C9.15497 2.01213 10.6935 2.48678 11.9248 3.444Z"
                                      fill="#F05203"
                                    />
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_6416_10007">
                                      <rect
                                        width="16"
                                        height="16"
                                        fill="white"
                                        transform="translate(0 0.5)"
                                      />
                                    </clipPath>
                                  </defs>
                                </svg>
                                Rejected
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="alert alert-info">
                          No attested certificates found.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-center justify-content-sm-between align-items-center custom-pagination-holder">
              <div className="mx-auto">
                <button
                  className="me-2 border-0 bg-transparent p-0 "
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    className={`bg-transparent border-0 ${
                      currentPage === i + 1
                        ? "pagination-item active"
                        : "pagination-item"
                    } me-2`}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="me-2 border-0 bg-transparent p-0"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>

              {/* Rows per page */}
              <div className="align-item-center d-none d-sm-flex">
                <label className="me-2 flex-shrink-0 align-self-center">
                  No of Rows
                </label>
                <select
                  className="form-control ms-16"
                  value={rowsPerPage}
                  onChange={handleRowsChange}
                  style={{ width: "100px" }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
          {/* Confirmation Modal */}
          <ConfirmationModal
            show={showModal}
            onHide={() => {
              setShowModal(false);
              setSelectedItem(null);
            }}
            title="Approve Certificate"
            message={`Are you sure you want to approve the certificate for ${selectedItem?.course?.title || selectedItem?.courseTitle}?`}
            onConfirm={handleApprove}
            confirmText="Approve Certificate"
          />
          <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
              <Modal.Title className="fw-semibold">
                Reason for Rejection
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                <label
                  htmlFor="rejectedReason"
                  className="form-label ah-text-base fw-semibold text-black-100"
                >
                  Reason
                </label>
                <textarea
                  id="rejectedReason"
                  className={`form-control ${declineError ? "is-invalid" : ""}`}
                  rows="4"
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Enter reason for rejection"
                ></textarea>
                {declineError && (
                  <div className="invalid-feedback">{declineError}</div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                className="w-100 py-10 d-flex justify-content-center align-items-center"
                onClick={handleDecline}
                disabled={declineLoading}
              >
                {declineLoading ? <DottedLoading /> : "Submit"}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default CertificatesApprovalsTable;
